import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Message as MessageIcon,
  Send as SendIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import type { UserDetails } from '@/types/team';

interface UserStatisticsProps {
  user: UserDetails;
}

// Helper function to get user statistics
const getUserStats = (user: UserDetails) => {
  if (!user.statistics) {
    return {
      total_leads: 0,
      scheduled_meetings: 0,
      scheduled_meetings_since_yesterday: 0,
      meeting_accepted_count: 0,
      acceptance_rate: 0,
      response_rate: 0,
      accepted_connections: 0,
      messages_received: 0,
      sent_invites: 0,
      sent_messages: 0,
      waiting_connections: 0,
      failed_jobs: 0,
      failed_jobs_yesterday: 0,
      prospect_estimation_days: 0,
    };
  }
  
  const stats = user.statistics;
  
  return {
    total_leads: stats.total_leads || 0,
    scheduled_meetings: stats.scheduled_meetings || 0,
    scheduled_meetings_since_yesterday: stats.scheduled_meetings_since_yesterday || 0,
    meeting_accepted_count: stats.meeting_accepted_count || 0,
    acceptance_rate: stats.acceptance_rate || 0, // Already in percentage from backend
    response_rate: stats.response_rate || 0, // Already in percentage from backend
    accepted_connections: stats.accepted_connections || 0, // Direct value from backend
    messages_received: stats.messages_received || 0, // Direct value from backend
    sent_invites: stats.sent_invites || 0, // Direct value from backend
    sent_messages: stats.sent_messages || 0, // Direct value from backend
    waiting_connections: stats.waiting_connections || 0, // Direct value from backend - waiting for connection acceptance
    failed_jobs: stats.failed_jobs || 0, // Direct value from backend - prospects with FAILED status
    failed_jobs_yesterday: stats.failed_jobs_yesterday || 0,
    prospect_estimation_days: stats.prospect_estimation_days || 0,
  };
};

const HintIcon: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="top">
    <HelpOutlineIcon sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5, cursor: 'help' }} />
  </Tooltip>
);

export const UserStatistics: React.FC<UserStatisticsProps> = ({ user }) => {
  const userStats = getUserStats(user);

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={3}>User Statistics</Typography>
        
        {/* First Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <PeopleIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.total_leads}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Total Leads</Typography>
              <HintIcon title="Total number of prospects for this user" />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <ScheduleIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.meeting_accepted_count}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">
                Accepted meetings ({userStats.messages_received > 0 ? ((userStats.meeting_accepted_count / userStats.messages_received) * 100).toFixed(1) : '0'}%)
              </Typography>
              <HintIcon title="Chats tagged by AI as &quot;Meeting Accepted&quot;." />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <ScheduleIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.scheduled_meetings}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Scheduled Meetings</Typography>
              <HintIcon title={`Total scheduled meetings (yesterday: +${userStats.scheduled_meetings_since_yesterday})`} />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.accepted_connections}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">
                Accepted ({userStats.acceptance_rate.toFixed(1)}%)
              </Typography>
              <HintIcon title={`Percentage: ${userStats.acceptance_rate.toFixed(1)}% | Count: ${userStats.accepted_connections}`} />
            </Box>
          </Box>
        </Box>

        {/* Second Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <MessageIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.messages_received}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">
                Messages Received ({userStats.response_rate.toFixed(1)}%)
              </Typography>
              <HintIcon title={`Percentage: ${userStats.response_rate.toFixed(1)}% | Count: ${userStats.messages_received}`} />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <SendIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.sent_invites}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Sent Connections</Typography>
              <HintIcon title={`Total sent connections (estimation: ${userStats.prospect_estimation_days} days)`} />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <ErrorIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.failed_jobs}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Failed Jobs</Typography>
              <HintIcon title={`Total failed jobs (yesterday: +${userStats.failed_jobs_yesterday})`} />
            </Box>
          </Box>
        </Box>

        {/* Third Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 2 }}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.prospect_estimation_days}
            </Typography>
            <Typography color="text.secondary">Days Estimation</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.waiting_connections}
            </Typography>
            <Typography color="text.secondary">Waiting Response</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h4" fontWeight={700} color="text.primary">
              {userStats.sent_messages}
            </Typography>
            <Typography color="text.secondary">Sent Messages</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
